import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid2,
	Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import supabase from "../../supabaseClient";
import { getImageUrl } from "../../utils/imageUrl";
import { createPlantSlug } from "../../utils/slugify";

/**
 * Individual plant detail page.
 *
 * URL pattern: /plants/:plantSlug
 *
 * Looks up the plant by its stored `slug` column first; falls back to
 * constructing a slug from plant_variety + plant_name so the page works
 * even before the nightly cron has populated the slug column.
 */
const PlantDetail = () => {
	const { plantSlug } = useParams();
	const navigate = useNavigate();

	const [plant, setPlant] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		ReactGA.send({ hitType: "pageview", page: `/plants/${plantSlug}` });
	}, [plantSlug]);

	useEffect(() => {
		const fetchPlant = async () => {
			setLoading(true);
			setNotFound(false);

			try {
				// First, try a direct slug lookup (fast once cron has run).
				const { data: bySlug } = await supabase
					.from("plants")
					.select("*, store_item_plants(store_items(price_cents, active, quantity_on_hand, reorder_level))")
					.eq("slug", plantSlug)
					.maybeSingle();

				if (bySlug) {
					setPlant(bySlug);
					setLoading(false);
					return;
				}

				// Fallback: fetch all plants and match by computed slug so the page
				// works before the cron job has populated the slug column.
				const { data: allPlants, error } = await supabase
					.from("plants")
					.select("*, store_item_plants(store_items(price_cents, active, quantity_on_hand, reorder_level))");

				if (error) throw error;

				const matched = (allPlants || []).find(
					(p) =>
						createPlantSlug(p.plant_variety, p.plant_name) === plantSlug,
				);

				if (matched) {
					setPlant(matched);
				} else {
					setNotFound(true);
				}
			} catch (err) {
				console.error("Error loading plant:", err);
				setNotFound(true);
			}

			setLoading(false);
		};

		if (plantSlug) fetchPlant();
	}, [plantSlug]);

	if (loading) {
		return (
			<Box sx={{ textAlign: "center", mt: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (notFound || !plant) {
		return (
			<Box sx={{ textAlign: "center", mt: 6 }}>
				<Typography variant="h5" gutterBottom>
					Plant not found
				</Typography>
				<Button variant="outlined" onClick={() => navigate("/plants")}>
					Back to all plants
				</Button>
			</Box>
		);
	}

	const pageTitle = `${plant.plant_variety} ${plant.plant_name} | Gosell Gardens`;

	// Derive price and inventory from the first active linked store item.
	const linkedStoreItem =
		plant.store_item_plants?.find((sip) => sip.store_items?.active)
			?.store_items ??
		plant.store_item_plants?.[0]?.store_items;
	const linkedPriceCents = linkedStoreItem?.price_cents;
	const displayPrice = linkedPriceCents != null ? linkedPriceCents / 100 : null;

	// Availability: derived from quantity_on_hand; fall back to "in stock" when
	// no store item is linked yet (e.g. catalog plants not yet sold in store).
	const quantityOnHand = linkedStoreItem?.quantity_on_hand;
	const reorderLevel = linkedStoreItem?.reorder_level ?? 0;
	const isAvailable = quantityOnHand == null || quantityOnHand > 0;

	const metaDescription =
		plant.seo_description ||
		plant.plant_description ||
		`Buy ${plant.plant_variety} ${plant.plant_name} at Gosell Gardens in Inver Grove Heights, MN. ${plant.plant_type ? plant.plant_type + " plant." : ""} ${plant.pot_size ? "Pot size: " + plant.pot_size + "." : ""}`.trim();

	// JSON-LD structured data helps Google understand this is a product page.
	const structuredData = {
		"@context": "https://schema.org",
		"@type": "Product",
		name: `${plant.plant_variety} ${plant.plant_name}`,
		description: metaDescription,
		image: plant.photo_url ? getImageUrl(plant.photo_url) : undefined,
		offers: displayPrice != null
			? {
					"@type": "Offer",
					priceCurrency: "USD",
					price: displayPrice.toFixed(2),
					availability: isAvailable
						? "https://schema.org/InStock"
						: "https://schema.org/OutOfStock",
					seller: {
						"@type": "LocalBusiness",
						name: "Gosell Gardens",
						address: {
							"@type": "PostalAddress",
							addressLocality: "Inver Grove Heights",
							addressRegion: "MN",
							addressCountry: "US",
						},
					},
			  }
			: undefined,
	};

	return (
		<Box sx={{ maxWidth: 800, mx: "auto", px: 2, py: 4 }}>
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={metaDescription} />
				<meta
					name="keywords"
					content={`${plant.plant_variety} ${plant.plant_name}, buy plants Inver Grove Heights, ${plant.plant_type || "plant"} for sale MN, Gosell Gardens`}
				/>
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={metaDescription} />
				{plant.photo_url && (
					<meta
						property="og:image"
						content={getImageUrl(plant.photo_url)}
					/>
				)}
				<meta
					property="og:url"
					content={`https://gosellgardens.com/plants/${plantSlug}`}
				/>
				<meta property="og:type" content="product" />
				<link
					rel="canonical"
					href={`https://gosellgardens.com/plants/${plantSlug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify(structuredData)}
				</script>
			</Helmet>

			{/* Breadcrumb */}
			<Box sx={{ mb: 2 }}>
				<Button
					variant="text"
					size="small"
					onClick={() => navigate("/plants")}
					sx={{ textTransform: "none", p: 0 }}
				>
					← All Plants
				</Button>
			</Box>

			<Grid2 container spacing={4}>
				{/* Photo */}
				{plant.photo_url && (
					<Grid2 size={{ xs: 12, sm: 5 }}>
						<Box
							component="img"
							src={getImageUrl(plant.photo_url)}
							alt={`${plant.plant_variety} ${plant.plant_name}`}
							loading="eager"
							sx={{
								width: "100%",
								borderRadius: 2,
								boxShadow: 2,
								objectFit: "cover",
							}}
						/>
					</Grid2>
				)}

				{/* Details */}
				<Grid2 size={{ xs: 12, sm: plant.photo_url ? 7 : 12 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
						<Typography variant="h4" component="h1">
							{plant.plant_variety}
						</Typography>
						{quantityOnHand != null ? (
							quantityOnHand === 0 ? (
								<Chip label="Out of Stock" color="error" size="small" />
							) : quantityOnHand <= reorderLevel ? (
								<Chip label="Low Stock" color="warning" size="small" />
							) : (
								<Chip label="In Stock" color="success" size="small" />
							)
						) : null}
					</Box>

					<Typography variant="h5" color="text.secondary" gutterBottom>
						{plant.plant_name}
					</Typography>

					{displayPrice != null && (
						<Typography variant="h6" gutterBottom>
							${displayPrice.toFixed(2)}
						</Typography>
					)}

					{plant.plant_type && (
						<Typography variant="body2" color="text.secondary" gutterBottom>
							Type: {plant.plant_type}
						</Typography>
					)}

					{plant.pot_size && (
						<Typography variant="body2" color="text.secondary" gutterBottom>
							Pot size: {plant.pot_size}
						</Typography>
					)}

					{plant.plant_description && (
						<Typography variant="body1" sx={{ mt: 1 }}>
							{plant.plant_description}
						</Typography>
					)}

					{plant.seo_description && (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mt: 2 }}
						>
							{plant.seo_description}
						</Typography>
					)}

					<Box sx={{ mt: 3 }}>
						<Button
							variant="contained"
							onClick={() => navigate("/plants")}
						>
							Browse All Plants
						</Button>
					</Box>
				</Grid2>
			</Grid2>

			{/* Growing tips section for SEO content depth */}
			<Box
				component="article"
				sx={{ mt: 6, borderTop: "1px solid", borderColor: "divider", pt: 4 }}
			>
				<Typography variant="h5" component="h2" gutterBottom>
					About {plant.plant_variety} {plant.plant_name}
				</Typography>
				<Typography variant="body1" paragraph>
					At <strong>Gosell Gardens</strong> in Inver Grove Heights, Minnesota,
					we grow our {plant.plant_variety} {plant.plant_name}{" "}
					{plant.plant_type ? `(${plant.plant_type})` : ""} with care. Whether
					you're an experienced gardener or just starting out, this variety is a
					wonderful addition to any Minnesota garden.
				</Typography>
				<Typography variant="body1" paragraph>
					Visit our{" "}
					<a href="/farmstand" style={{ color: "#1976d2" }}>
						farm stand
					</a>{" "}
					in Inver Grove Heights to pick up this plant in person, or{" "}
					<a href="/plants" style={{ color: "#1976d2" }}>
						browse our full selection
					</a>{" "}
					to see everything we have growing this season.
				</Typography>
			</Box>
		</Box>
	);
};

export default PlantDetail;
